import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { currentHost, type HostInfo } from './shared/config/host'
import { SurfaceRoot } from './shared/ui'

// Each area lazy-loads so the public web bundle stays small. Studio and
// platform are only fetched when the user actually navigates into them.
const WebApp = lazy(() => import('./areas/web/WebApp'))
const StudioApp = lazy(() => import('./areas/studio/StudioApp'))
const PlatformApp = lazy(() => import('./areas/platform/PlatformApp'))

// AppRoutes is exported so tests can mount it under a MemoryRouter and drive
// route-based behavior without spinning up a real history. `host` is injectable
// for the same reason.
//
// The default `/` route is HOST-AWARE: the white-label learner surface only
// belongs on a tenant academy host ({slug}.<apex> or a custom domain). On the
// control plane (app.<apex>, the apex, or a dev/local host) there is no tenant,
// so `/` routes to the Studio instead of the learner site that would otherwise
// have nothing to show. /studio and /platform stay reachable on every host.
export function AppRoutes({ host = currentHost }: { host?: HostInfo } = {}) {
  return (
    <Suspense fallback={<AreaLoader />}>
      <Routes>
        <Route path="/studio/*" element={<StudioApp />} />
        <Route path="/platform/*" element={<PlatformApp />} />
        <Route
          path="/*"
          element={host.isControlPlane ? <Navigate to="/studio" replace /> : <WebApp />}
        />
      </Routes>
    </Suspense>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}

function AreaLoader() {
  return (
    <SurfaceRoot
      role="status"
      aria-label="Loading area"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--c-fg-muted)',
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}
    >
      Loading
    </SurfaceRoot>
  )
}
